import { Component, Inject, signal, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { ActivatedRoute, Router } from '@angular/router';
import { WINDOW } from '@core/injectors';
import { DataSourceApprovalStatus } from '@core/models';
import {
  CustomSortingTableDataSource,
  initializeSearchFromParams,
  isMatchingDataSourceApprovalStatus,
  sortingColumns,
} from '@core/utils';
import {
  isConnectorTooltipDisabled,
  isDataSourceTooltipDisabled,
  isPipelineTooltipDisabled,
} from '@core/utils/check-status-for-tooltip-disable';
import { ProcessIssuesStatusStringMap } from '@shared';
import { debounceTime, distinctUntilChanged, map, Observable, Subscription, switchMap } from 'rxjs';
import { ProcessIssueItem, ProcessIssuesDTO } from '../../models';
import { IssuesDashboardDataService } from '../../services/issues-dashboard-data/issues-dashboard-data.service';

interface StatusFilterOption {
  value: string;
  label: string;
}

// Every raw status value that can actually surface as the displayed "status" of a Pipeline,
// Connector, or Data Source row in this table. DELETED is excluded from all three querysets
// server-side, and CONNECTED is superseded by approval_status, so neither is ever displayed.
const REACHABLE_STATUSES = [
  // Pipeline
  'PENDING_APPROVAL',
  'APPROVED',
  'PUBLISHED',
  'ERRORS',
  'QUEUED',
  'RUNNING',
  'PAUSING',
  'PAUSED',
  'RESUMING',
  'PENDING_NEXT_RUN',
  // Data Source (connect_status)
  'PENDING_TECHNICAL_DETAILS',
  'PENDING_CONNECTION',
  'UPDATE_CATALOG',
  'DISCOVERY',
  'CONNECTION_FAILED',
  'CATALOG_INCOMPLETE',
  'DISCOVERY_INCOMPLETE',
  // Data Source (approval_status, once connected)
  'NEW',
  'CATALOGUED',
  'UNDER_DISCOVER',
  // Connector
  'PENDING',
  'AUTOMATED',
  'NEED_SCHEMA_APPROVAL',
  'PENDING_SCHEMA_APPROVAL',
  'SUCCESS',
  'FAILURE',
];

// Some raw statuses share a single display label (e.g. Connector's PENDING and Pipeline's
// PENDING_APPROVAL both show as "Pending Approval"). Group them into one filter option whose
// value is the comma-separated list of underlying raw values, so the dropdown has no duplicate
// labels while still matching every status that label represents. Options are sorted
// alphabetically by label so the dropdown reads in a predictable order.
function buildStatusFilterOptions(rawValues: string[]): StatusFilterOption[] {
  const rawValuesByLabel = new Map<string, string[]>();
  for (const raw of rawValues) {
    const label = ProcessIssuesStatusStringMap.get(raw) || raw;
    rawValuesByLabel.set(label, [...(rawValuesByLabel.get(label) || []), raw]);
  }
  return Array.from(rawValuesByLabel.entries())
    .map(([label, values]) => ({ value: values.join(','), label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

@Component({
  selector: 'app-process-issues-table',
  templateUrl: './process-issues-table.component.html',
  styleUrl: './process-issues-table.component.scss',
  standalone: false,
})
export class ProcessIssuesTableComponent {
  dataList$!: Observable<CustomSortingTableDataSource<ProcessIssueItem>>;

  isLoading = signal<boolean>(false);
  currentPage = signal<number>(0);
  pageSize = signal<number>(0);
  ordering = signal<string[]>([]);
  resultsLength = signal<number>(0);

  formSearch = new FormControl('');
  filterCustomerName = new FormControl('');
  filterObjectType = new FormControl('');
  filterObjectName = new FormControl('');
  filterStatus = new FormControl('');

  displayedColumns = ['customer_name', 'object_type', 'object_name', 'status', 'last_run_datetime', 'actions'];
  filterColumns = [
    'filter_customer_name',
    'filter_object_type',
    'filter_object_name',
    'filter_status',
    'filter_last_run_datetime',
    'filter_actions',
  ];
  ALLOWED_SORT_COLUMNS = new Set([
    'customer_id',
    'customer_name',
    'object_type',
    'object_name',
    'status',
    'last_run_datetime',
  ]);

  readonly objectTypeOptions = ['Connector', 'Pipeline', 'Data Source'];
  readonly statusOptions: StatusFilterOption[] = buildStatusFilterOptions(REACHABLE_STATUSES);

  private sortSubscription!: Subscription;
  private filterSubscriptions: Subscription[] = [];
  private readonly relevantParamKeys = [
    'pi_page',
    'page_size',
    'pi_search',
    'pi_customer_name',
    'pi_object_type',
    'pi_object_name',
    'pi_status',
  ];

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    private issuesDashboardDataService: IssuesDashboardDataService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    @Inject(WINDOW) private readonly win: Window,
  ) {}

  ngOnInit(): void {
    this.route.queryParams
      .pipe(distinctUntilChanged((prev, curr) => this.relevantParamKeys.every((key) => prev[key] === curr[key])))
      .subscribe((params) => {
        this.currentPage.set(+params['pi_page'] || 1);
        this.pageSize.set(+params['page_size'] || 20);
        initializeSearchFromParams(this.formSearch, params['pi_search'] || '');
        initializeSearchFromParams(this.filterCustomerName, params['pi_customer_name'] || '');
        initializeSearchFromParams(this.filterObjectType, params['pi_object_type'] || '');
        initializeSearchFromParams(this.filterObjectName, params['pi_object_name'] || '');
        initializeSearchFromParams(this.filterStatus, params['pi_status'] || '');
        this.updateList();
      });
  }

  ngAfterViewInit() {
    this.sortSubscription = sortingColumns(this.sort, this.currentPage, this.ordering, this.ALLOWED_SORT_COLUMNS, () =>
      this.updateList(),
    );
    this.filterSubscriptions = [
      this.setupFilterParam(this.formSearch, 'pi_search'),
      this.setupFilterParam(this.filterCustomerName, 'pi_customer_name'),
      this.setupFilterParam(this.filterObjectType, 'pi_object_type'),
      this.setupFilterParam(this.filterObjectName, 'pi_object_name'),
      this.setupFilterParam(this.filterStatus, 'pi_status'),
    ];
  }

  private setupFilterParam(control: FormControl, paramKey: string): Subscription {
    return control.valueChanges.pipe(debounceTime(500), distinctUntilChanged()).subscribe((value) => {
      this.router.navigate([], {
        queryParams: { [paramKey]: (value || '').trim(), pi_page: 1 },
        queryParamsHandling: 'merge',
      });
    });
  }

  isTooltipDisabled(type: string, status: string) {
    let formattedStatus = status.replace(/_/g, ' ');
    switch (type) {
      case 'pipeline':
        return isPipelineTooltipDisabled(formattedStatus);
      case 'connector':
        return isConnectorTooltipDisabled(formattedStatus);
      case 'data source':
        return isDataSourceTooltipDisabled(formattedStatus);
      default:
        return false;
    }
  }

  private updateList(): void {
    const params = this.route.snapshot.queryParams;
    this.isLoading.set(true);
    this.dataList$ = this.issuesDashboardDataService
      .getProcessIssues(
        this.currentPage(),
        this.pageSize(),
        this.ordering().join(','),
        params['pi_search'] || '',
        params['pi_object_type'] || '',
        params['pi_status'] || '',
        params['pi_customer_name'] || '',
        params['pi_object_name'] || '',
      )
      .pipe(
        map((data: ProcessIssuesDTO) => {
          this.resultsLength.set(data.count);
          this.isLoading.set(false);
          return new CustomSortingTableDataSource<ProcessIssueItem>(data.results);
        }),
      );
  }

  redirectToZendeskTicket(zendeskTicketUrl?: string) {
    if (zendeskTicketUrl) {
      this.win.open(zendeskTicketUrl, '_blank');
    }
  }

  onPageChange(event: any) {
    const { pageIndex, pageSize } = event;
    this.currentPage.set(pageIndex + 1);
    this.pageSize.set(pageSize);
    this.updateList();
  }

  navigateToItem(id: number, customerId: number, item: string, approvalStatus?: string) {
    const itemMap: Record<string, string> = {
      connector: 'connectors',
      pipeline: 'pipelines',
      'data source': 'data-sources',
    };

    const route = itemMap[item.toLowerCase()];

    if (
      item.toLowerCase() === 'data source' &&
      approvalStatus &&
      isMatchingDataSourceApprovalStatus(approvalStatus, DataSourceApprovalStatus.New)
    ) {
      this.router.navigate([customerId, route], { relativeTo: this.route });
    } else {
      this.router.navigate([customerId, route, id], { relativeTo: this.route });
    }
  }

  getPipelineTooltip(pipelineNames: string[]): string {
    return `Used in Pipelines:\n${pipelineNames.map((name) => `\u00A0\u00A0• ${name}`).join('\n')}`;
  }

  ngOnDestroy(): void {
    this.sortSubscription?.unsubscribe();
    this.filterSubscriptions.forEach((sub) => sub.unsubscribe());
  }
}
